import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import ApperIcon from '@/components/ApperIcon';
import LookupPicker from '@/components/LookupPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Busy, Field, Panel } from '@/components/kit';
import { PickSelect, SwitchRow } from '@/components/inputs';
import { CONTACT_METHOD_VALUES, ORDER_STATUSES } from '@/shared/constants';
import { CUSTOMER_FIELDS, PRODUCT_FIELDS } from '@/shared/fields';
import {
  ORDER_PLAN_VALUES,
  PAYMENT_METHODS,
  methodValuesWith,
  PLANS,
  TELLY,
  contactPlaceholder,
  money,
  newCode,
  productPrice,
  quote,
  termsFor,
} from '@/shared/pricing';
import { fromLocalInput, idOf, toLocalInput } from '@/shared/data';

const TEXT = [
  'full_name_c',
  'email_c',
  'contact_handle_c',
  'device_c',
  'discount_code_c',
  'currency_c',
  'line_server_c',
  'line_username_c',
  'line_password_c',
  'line_note_c',
  'telly_key_c',
  'notes_c',
];
const PICKS = ['contact_method_c', 'plan_c', 'term_c', 'payment_method_c', 'status_c'];
const NUMS = ['connections_c', 'price_c', 'discount_c', 'telly_qty_c'];
const BOOLS = ['adult_content_c', 'wants_trial_c', 'include_epg_c', 'telly_addon_c'];
const DATES = [
  ['activated_at_c', 'Activated'],
  ['ends_at_c', 'Term ends'],
  ['renew_requested_at_c', 'Renewal requested'],
  ['refund_accepted_at_c', 'No-refund policy accepted'],
  ['credentials_sent_at_c', 'Credentials sent'],
  ['telly_sent_at_c', 'Licence key sent'],
  ['expiry_emailed_at_c', 'Expiry reminder sent'],
  ['winback_emailed_at_c', 'Win-back offer sent'],
];

const METHOD_LABELS = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.label]));
const PLAN_LABELS = Object.fromEntries(ORDER_PLAN_VALUES.map((p) => [p, p === 'Product' ? 'Store product' : (PLANS[p]?.name ?? p)]));

export function orderToForm(order) {
  const form = { Name: order?.Name ?? '', customer_c: idOf(order?.customer_c), product_c: idOf(order?.product_c) };
  [...TEXT, ...PICKS].forEach((k) => {
    form[k] = order?.[k] ?? '';
  });
  NUMS.forEach((k) => {
    form[k] = order?.[k] == null ? '' : String(order[k]);
  });
  BOOLS.forEach((k) => {
    form[k] = !!order?.[k];
  });
  DATES.forEach(([k]) => {
    form[k] = toLocalInput(order?.[k]);
  });
  return form;
}

export function blankOrder(customerId) {
  return {
    ...orderToForm(null),
    Name: newCode(),
    customer_c: customerId || null,
    contact_method_c: 'Discord',
    plan_c: 'Demon',
    term_c: '1y',
    connections_c: '1',
    price_c: String(quote('Demon', '1y', 1)),
    discount_c: '0',
    currency_c: 'USD',
    payment_method_c: 'Card',
    status_c: 'Pending',
    telly_qty_c: '0',
  };
}

export function formToPayload(form) {
  const out = {
    Name: form.Name.trim().toUpperCase(),
    customer_c: form.customer_c || null,
    product_c: form.plan_c === 'Product' ? idOf(form.product_c) : null,
  };
  TEXT.forEach((k) => {
    out[k] = String(form[k] ?? '').trim();
  });
  PICKS.forEach((k) => {
    out[k] = form[k] || null;
  });
  NUMS.forEach((k) => {
    out[k] = Number(form[k]) || 0;
  });
  BOOLS.forEach((k) => {
    out[k] = !!form[k];
  });
  DATES.forEach(([k]) => {
    out[k] = fromLocalInput(form[k]);
  });
  return out;
}

function listPrice(form, product) {
  if (form.plan_c === 'Product') return productPrice(product, form.term_c);
  const base = quote(form.plan_c, form.term_c, Number(form.connections_c));
  if (base == null) return null;
  const telly =
    form.plan_c !== 'Telly' && form.telly_addon_c ? Math.max(1, Number(form.telly_qty_c) || 1) * TELLY.price : 0;
  return base + telly;
}

/** The full order form, shared by the New and Edit pages. `onSubmit` receives the write payload. */
export default function OrderForm({ initial, onSubmit, saving, submitLabel = 'Save order', busyLabel = 'Saving…', cancelTo }) {
  const [form, setForm] = useState(initial);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);
  const [productRow, setProductRow] = useState(null);
  const terms = termsFor(form.plan_c);
  const suggested = listPrice(form, productRow);
  const isTelly = form.plan_c === 'Telly';
  const isProduct = form.plan_c === 'Product';

  useEffect(() => {
    const id = idOf(form.product_c);
    if (!isProduct || !id) {
      setProductRow(null);
      return undefined;
    }
    let live = true;
    sdk
      .table('products_c')
      .select(PRODUCT_FIELDS)
      .get(id)
      .then((r) => {
        if (live) setProductRow(r?.data ?? null);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [isProduct, form.product_c]);

  async function fillFromCustomer(id) {
    const r = await sdk.table('customers_c').select(CUSTOMER_FIELDS).get(id);
    const c = r?.data;
    if (!c) return;
    setForm((f) => ({
      ...f,
      full_name_c: f.full_name_c || c.Name || '',
      email_c: f.email_c || c.email_c || '',
      contact_method_c: f.contact_handle_c ? f.contact_method_c : c.contact_method_c || f.contact_method_c,
      contact_handle_c: f.contact_handle_c || c.contact_handle_c || '',
    }));
  }

  useEffect(() => {
    if (initial.customer_c && !initial.email_c) fillFromCustomer(initial.customer_c);
  }, []);

  function pickCustomer(id) {
    set('customer_c')(id || null);
    if (id) fillFromCustomer(id);
  }

  function pickPlan(plan) {
    setForm((f) => {
      const options = termsFor(plan);
      const term = options.some((t) => t.id === f.term_c) ? f.term_c : options[0].id;
      const connections = plan === 'Wraith' || plan === 'Product' ? '1' : f.connections_c || '1';
      return { ...f, plan_c: plan, term_c: term, connections_c: connections };
    });
  }

  function applyListPrice() {
    if (suggested == null) {
      toast.error('That plan, term and connection count is not sold');
      return;
    }
    set('price_c')(String(suggested));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.Name.trim()) return toast.error('Give the order a code');
    if (!form.email_c.trim()) return toast.error('Add the customer email');
    if (!form.plan_c || !form.term_c) return toast.error('Pick a plan and a term');
    if (form.plan_c === 'Product' && !idOf(form.product_c)) return toast.error('Pick the store product');
    if (!(Number(form.connections_c) >= 1)) return toast.error('Connections must be at least 1');
    return onSubmit(formToPayload(form));
  }

  return (
    <form onSubmit={submit}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Order" icon="Package" bodyClassName="space-y-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Order code" htmlFor="o-code">
              <Input id="o-code" value={form.Name} onChange={text('Name')} placeholder="GC-7Q2MKD" className="font-mono uppercase" />
            </Field>
            <Field label="Status" htmlFor="o-status">
              <PickSelect id="o-status" value={form.status_c} onChange={set('status_c')} values={ORDER_STATUSES} placeholder="Choose a status" />
            </Field>
          </div>
          <Field label="Customer" htmlFor="o-customer" hint="Picking a customer fills in any empty contact fields.">
            <LookupPicker
              table="customers_c"
              searchField="Name"
              value={form.customer_c}
              onChange={pickCustomer}
              placeholder="Search customers…"
              clearable
            />
          </Field>
        </Panel>

        <Panel title="Contact" icon="Radio" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field label="Full name" htmlFor="o-name">
            <Input id="o-name" value={form.full_name_c} onChange={text('full_name_c')} placeholder="Maya Okafor" />
          </Field>
          <Field label="Email" htmlFor="o-email">
            <Input id="o-email" type="email" value={form.email_c} onChange={text('email_c')} placeholder="maya@example.com" />
          </Field>
          <Field label="Contact method" htmlFor="o-method">
            <PickSelect
              id="o-method"
              value={form.contact_method_c}
              onChange={set('contact_method_c')}
              values={CONTACT_METHOD_VALUES}
              placeholder="Discord, WhatsApp or Telegram"
            />
          </Field>
          <Field label="Handle" htmlFor="o-handle">
            <Input
              id="o-handle"
              value={form.contact_handle_c}
              onChange={text('contact_handle_c')}
              placeholder={contactPlaceholder(form.contact_method_c)}
            />
          </Field>
          <Field label="Device" htmlFor="o-device" className="sm:col-span-2">
            <Input id="o-device" value={form.device_c} onChange={text('device_c')} placeholder="Firestick 4K, LG webOS…" />
          </Field>
        </Panel>

        <Panel title="Package" icon="Tv" bodyClassName="space-y-4 py-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Plan" htmlFor="o-plan">
              <PickSelect id="o-plan" value={form.plan_c} onChange={pickPlan} values={ORDER_PLAN_VALUES} labels={PLAN_LABELS} placeholder="Choose a plan" />
            </Field>
            <Field label="Term" htmlFor="o-term">
              <PickSelect
                id="o-term"
                value={form.term_c}
                onChange={set('term_c')}
                values={terms.map((t) => t.id)}
                labels={Object.fromEntries(terms.map((t) => [t.id, t.label]))}
                placeholder="Choose a term"
              />
            </Field>
            <Field label={isTelly ? 'Licences' : 'Connections'} htmlFor="o-conn">
              <Input
                id="o-conn"
                type="number"
                min={1}
                max={isTelly ? TELLY.maxQty : 5}
                value={form.connections_c}
                onChange={text('connections_c')}
                placeholder="1"
              />
            </Field>
          </div>
          {isProduct && (
            <Field label="Store product" htmlFor="o-product" hint="Prices come from the product's term prices.">
              <LookupPicker
                table="products_c"
                searchField="Name"
                value={idOf(form.product_c)}
                onChange={(id) => set('product_c')(id || null)}
                placeholder="Search products…"
                clearable
              />
            </Field>
          )}
          {!isTelly && (
            <div className="grid gap-2">
              <SwitchRow id="o-adult" label="Adult content" checked={form.adult_content_c} onChange={set('adult_content_c')} />
              <SwitchRow id="o-trial" label="Wants a free trial first" checked={form.wants_trial_c} onChange={set('wants_trial_c')} />
              <SwitchRow id="o-epg" label="Premium TV guide (EPG)" checked={form.include_epg_c} onChange={set('include_epg_c')} />
              <SwitchRow
                id="o-telly"
                label={`${TELLY.name} add-on`}
                hint={`${money(TELLY.price)} per lifetime licence`}
                checked={form.telly_addon_c}
                onChange={set('telly_addon_c')}
              />
            </div>
          )}
          {!isTelly && form.telly_addon_c && (
            <Field label="Add-on licences" htmlFor="o-telly-qty">
              <Input
                id="o-telly-qty"
                type="number"
                min={1}
                max={TELLY.maxQty}
                value={form.telly_qty_c}
                onChange={text('telly_qty_c')}
                placeholder="1"
                className="w-28"
              />
            </Field>
          )}
        </Panel>

        <Panel title="Billing" icon="Wallet" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field
            label="Price (USD)"
            htmlFor="o-price"
            hint={suggested == null ? 'Not a sold combination' : `List price ${money(suggested)}`}
          >
            <div className="flex gap-2">
              <Input
                id="o-price"
                type="number"
                min={0}
                step="0.01"
                value={form.price_c}
                onChange={text('price_c')}
                placeholder="28"
              />
              <Button type="button" variant="outline" onClick={applyListPrice} aria-label="Use the list price">
                <ApperIcon name="Calculator" size={16} />
              </Button>
            </div>
          </Field>
          <Field label="Payment method" htmlFor="o-pay">
            <PickSelect
              id="o-pay"
              value={form.payment_method_c}
              onChange={set('payment_method_c')}
              values={methodValuesWith(form.payment_method_c)}
              labels={METHOD_LABELS}
              placeholder="How they will pay"
            />
          </Field>
          <Field label="Discount code" htmlFor="o-code-used">
            <Input
              id="o-code-used"
              value={form.discount_code_c}
              onChange={text('discount_code_c')}
              placeholder="BOO10"
              className="uppercase"
            />
          </Field>
          <Field label="Discount (USD)" htmlFor="o-discount">
            <Input
              id="o-discount"
              type="number"
              min={0}
              step="0.01"
              value={form.discount_c}
              onChange={text('discount_c')}
              placeholder="0"
            />
          </Field>
          <Field label="Currency" htmlFor="o-currency" hint="Prices are kept in USD.">
            <Input id="o-currency" value={form.currency_c} onChange={text('currency_c')} placeholder="USD" className="uppercase" />
          </Field>
        </Panel>

        <Panel title="Line details" icon="KeyRound" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field label="Server URL" htmlFor="o-server" className="sm:col-span-2">
            <Input id="o-server" value={form.line_server_c} onChange={text('line_server_c')} placeholder="http://line.ghostcable.app:8080" />
          </Field>
          <Field label="Username" htmlFor="o-user">
            <Input id="o-user" value={form.line_username_c} onChange={text('line_username_c')} placeholder="maya4413" />
          </Field>
          <Field label="Password" htmlFor="o-pass">
            <Input id="o-pass" value={form.line_password_c} onChange={text('line_password_c')} placeholder="k7Qp2vXz" />
          </Field>
          <Field label="Note for the customer" htmlFor="o-line-note" className="sm:col-span-2">
            <Textarea
              id="o-line-note"
              rows={2}
              value={form.line_note_c}
              onChange={text('line_note_c')}
              placeholder="Restart the app after adding the playlist…"
            />
          </Field>
        </Panel>

        <Panel title={`${TELLY.name} key`} icon="MonitorPlay" bodyClassName="py-4">
          <Field label="Licence key" htmlFor="o-telly-key" hint="One key per line when there are several licences.">
            <Textarea
              id="o-telly-key"
              rows={3}
              value={form.telly_key_c}
              onChange={text('telly_key_c')}
              placeholder="DTLY-4K9Q-7MZP-2RXV"
              className="font-mono"
            />
          </Field>
        </Panel>

        <Panel title="Dates and reminders" icon="CalendarClock" className="lg:col-span-2" bodyClassName="grid gap-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          {DATES.map(([key, label]) => (
            <Field key={key} label={label} htmlFor={`o-${key}`}>
              <Input
                id={`o-${key}`}
                type="datetime-local"
                value={form[key]}
                onChange={text(key)}
                placeholder="2026-11-02T20:00"
              />
            </Field>
          ))}
        </Panel>

        <Panel title="Internal notes" icon="NotebookPen" className="lg:col-span-2" bodyClassName="py-4">
          <Field label="Notes" htmlFor="o-notes">
            <Textarea
              id="o-notes"
              rows={3}
              value={form.notes_c}
              onChange={text('notes_c')}
              placeholder="Two TVs in different rooms, prefers evening contact…"
            />
          </Field>
        </Panel>
      </div>

      <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-2 border-t border-border bg-background/95 py-3 backdrop-blur">
        <Button type="button" variant="outline" asChild>
          <Link to={cancelTo}>Cancel</Link>
        </Button>
        <Button type="submit" disabled={saving}>
          <Busy busy={saving} label={submitLabel} busyLabel={busyLabel} />
        </Button>
      </div>
    </form>
  );
}
